import { $authHost } from 'app/configs/axiosConfig';
import {
  AdminFilters,
  FilteredMerchants,
  ICreateNovoAccountResponse,
  ILocalStoreKey,
  IMerchantCreateRequest,
  IMerchantDetailsResponse,
  IMerchantIncompleteRequest,
  IMerchantOnboardingResponse,
  IMerchantUpdateRequest,
} from '../types';
import { getUTMParams, pixelTrackEvent } from './FacebookTrackingApi';
import { captureEvent } from 'src/posthog';

declare global {
  interface Window {
    dataLayer?: { event: string; eventId?: string }[];
  }
}

export const createMerchant = async (merchant: IMerchantCreateRequest) => {
  const eventId = crypto.randomUUID();

  pixelTrackEvent('CompleteRegistration', eventId);

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'RegistrationCompleted',
      eventId,
    });
    console.log('[GTM] Sent RegistrationCompleted event');
  }

  const utmParams = getUTMParams();

  localStorage.removeItem(ILocalStoreKey.loginQueryParamsData);

  const res = await $authHost.post<IMerchantOnboardingResponse>('/merchant', merchant, {
    headers: {
      'fb-event-id': eventId,
      tr_utm_source: utmParams.utmSource,
      tr_utm_medium: utmParams.utmMedium,
      tr_utm_campaign: utmParams.utmCampaign,
      tr_utm_content: utmParams.utmContent,
      tr_utm_term: utmParams.utmTerm,
    },
  });

  // After the POST resolves, unlike the pixel/GTM events above which fire on attempt.
  captureEvent('merchant_application_submitted', {
    event_id: eventId,
    business_structure: merchant.businessStructure,
    mcc: merchant.mcc,
    state: merchant.state,
    is_card_retail: merchant.isCardRetail,
    is_card_ecommerce: merchant.isCardECommerce,
    need_equipment: merchant.needEquipment,
  });

  return res.data;
};

export const createIncompleteMerchant = (data: IMerchantIncompleteRequest) => {
  return $authHost.post(`/merchant/save-incomplete`, data);
};

export const getFilteredMerchants = (filters: AdminFilters) => {
  if (filters.where) {
    const where = filters.where as any;
    if (where.OR && Array.isArray(where.OR)) {
      where.OR = where.OR.map(processWhereCondition);
    }
    if (where.AND && Array.isArray(where.AND)) {
      where.AND = where.AND.map(processWhereCondition);
    }
  }
  return $authHost.post<FilteredMerchants>(`/merchant/filter`, filters);
};

const processWhereCondition = (condition: any): any => {
  if (!condition || typeof condition !== 'object') return condition;

  const processedCondition = { ...condition };

  Object.keys(processedCondition).forEach((key) => {
    const value = processedCondition[key];

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (
        typeof value.contains === 'string' ||
        typeof value.equals === 'string' ||
        typeof value.startsWith === 'string' ||
        typeof value.endsWith === 'string'
      ) {
        value.mode = 'insensitive';

        if (value.contains) value.contains = value.contains.trim().replace(/\s+/g, ' ');
        if (value.equals) value.equals = value.equals.trim().replace(/\s+/g, ' ');
        if (value.startsWith) value.startsWith = value.startsWith.trim().replace(/\s+/g, ' ');
        if (value.endsWith) value.endsWith = value.endsWith.trim().replace(/\s+/g, ' ');
      } else {
        processedCondition[key] = processWhereCondition(value);
      }
    }
  });

  return processedCondition;
};

export const updateMerchant = (merchant: IMerchantUpdateRequest, id: number) => {
  return $authHost.patch(`/merchant/${id}`, merchant);
};

export const completeGetStarted = () => {
  return $authHost.post(`/merchant/complete-get-started`);
};

export const getCurrentMerchant = () => {
  return $authHost.get<IMerchantDetailsResponse>('/merchant/me');
};

export const createNovoAccount = () => {
  return $authHost.get<ICreateNovoAccountResponse>('/merchant/create-novo-account');
};
